import React, { useState } from 'react';
import { ErrorBoundary as DatadogErrorBoundary } from '@datadog/browser-rum-react';
import { Button, Typography, Stack, DialogContent, DialogActions, Dialog, IconButton } from '@mui/material';
import { ReactComponent as WarningIcon } from '../assets/icons/warning.svg';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { T } from '../hooks/useTranslate';
import { useMixPanel } from '../hooks/useMixPanel';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Error boundary component that catches JavaScript errors anywhere in the child component tree.
 * Uses Datadog's ErrorBoundary for tracking errors.
 */
const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const [open, setOpen] = useState(true);
  const { logEvent } = useMixPanel();

  const handleReload = (error: Error) => {
    logEvent("error_boundary_reload", {
      error: error.message,
    })
    window.location.reload();
  };

  const handleClose = (error: Error) => {
    logEvent("error_boundary_close", {
      error: error.message,
    })
    setOpen(false);
  };

  const handleBackToHome = (error: Error) => {
    logEvent("error_boundary_back_to_home", {
      error: error.message,
    })
    window.location.href = `${window.location.pathname}#/`;
  };

  return (
    <DatadogErrorBoundary 
      test-id="error-boundary"
      fallback={({ error }) => (
        <Dialog
          test-id="error-boundary-dialog"
          open={open}
          onClose={() => {}}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
          }}
        >
          <DialogContent sx={{ padding: '16px 16px 0 16px' }}>
            <WarningIcon className="warning-icon" style={{ marginBottom: 1 }} />
            <IconButton
              onClick={() => handleClose(error)}
              test-id="close-market-selector"
              size="small"
              aria-label="close"
              data-test-id="close-market-selector"
              sx={{ position: 'absolute', top: 16, right: 16 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="h2" marginBottom={1} fontSize="12px">
              <T>error_boundary_title</T>
            </Typography>
            <Typography variant="body1" paragraph fontSize="10px">
              <T>error_boundary_message</T>
            </Typography>
            {process.env.NODE_ENV === 'development' && (
              <Typography variant="body2" color="error" paragraph fontSize="10px">
                Error: {error.message}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: '16px' }}>
            <Stack direction="row" spacing={2}>
              <Button 
                test-id="error-boundary-reload-button"
                variant="simple" 
                color="primary" 
                onClick={() => handleReload(error)}
                data-test-id="error-boundary-reload-button"
                sx={{ paddingX: 2 }}
                endIcon={<ChevronRightIcon height={12} width={12} />}
              >
                <T>reload_page</T>
              </Button>
              <Button 
                test-id="error-boundary-home-button"
                variant="simple" 
                onClick={() => handleBackToHome(error)}
                data-test-id="error-boundary-home-button"
                sx={{ paddingX: 2 }}
                endIcon={<ChevronRightIcon height={12} width={12} />}
              >
                <T>back_to_home</T>
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>
      )}
    >
      {children}
    </DatadogErrorBoundary>
  );
};

export default ErrorBoundary; 